import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router";
import { useSearchParams } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import styled from "styled-components";
import { ReactComponent as CalendarIcon } from "../../../assets/images/calender.svg";
import { APP_TITLE } from "../../../config/constant";
import { formatDefaultDateTime } from "../../../utils/Formatter";
import * as Library from "../../../utils/Library";
import FormContainer from "../../common/forms/FormContainer";
import FormField from "../../common/forms/FormField";
import FullScreenLoader from "../../common/loaders/FullScreenLoader";
import { CustomSearch } from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import ItemVariationsSection from "./productDetailsComponents/ItemVariationsSection";
import OtherInfoSection from "./productDetailsComponents/OtherInfoSection";
import { ProductImageCarousel } from "./productDetailsComponents/ProductImageCarousel";
import { ShelfSpeedAndStock } from "./productDetailsComponents/ShelfSpeedAndStock";

// #region  SCOPED CSS
const StyledPage = styled.div`
    &.disabled-form .card-title {
        color: var(--text-icons-gray-300) !important;
    }

    .form-container-tag {
        display: flex;
        padding: 3px;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        gap: 10px;
        border-radius: 2px;
        /* background: var(--Buttons-All-Platforms-btn-action-green, #080); */
        margin-left: 12px;
    }
    .top-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        height: 298px;
    }
    .inputs-container {
        display: flex;
        flex-direction: column;
        gap: 16px;
        width: 394px;
    }
    .last-update {
        color: var(--text-icons-gray-100, #d1d1d1);
        text-align: right;
        font-size: 12px;
        font-weight: 400;
        line-height: normal;
        text-transform: none;
    }
    .last-update-container {
        display: flex;
        flex-direction: rows;
        gap: 4px;
    }
`;
// #endregion

export default function ProductDetailsPage() {
    document.title = `${APP_TITLE} - Product Details`;

    // #region VARS
    const [searchParams] = useSearchParams();
    const identifierID = searchParams.get("productID") || searchParams.get("gtinID");
    const isGTIN = searchParams.get("gtinID") !== null;
    const productID = searchParams.get("productID");
    const productName = searchParams.get("productName");

    const navigations = useSelector(state => state.navigationAccesses);
    const createMode = navigations?.navigationAccesses?.products?.createModeOnly;
    const { MainLayoutProps, layoutType } = useOutletContext();
    const navigate = useNavigate();

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };
    // #endregion

    // #region STATES
    const [isLoadingProduct, setIsLoadingProduct] = useState(false);
    const [pageEnabled, setPageEnabled] = useState(false);
    const [shouldUpdate, setShouldUpdate] = useState(false);

    const [productData, setProductData] = useState(null);
    const [currentProduct, setCurrentProduct] = useState(null);
    const [searchText, setSearchText] = useState("");
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        if (!identifierID || !shouldUpdate) return;
        getProductDetails();

        if (shouldUpdate) setShouldUpdate(false);
    }, [shouldUpdate, identifierID]); // Fetch product details on update request

    useEffect(() => {
        if (!identifierID) return;

        getProductDetails();
    }, []); // Fetch product details on mount

    useEffect(() => {
        setupHeaders(
            props,
            createMode,
            navigate,
            setShouldUpdate,
            identifierID,
            currentProduct,
            searchText,
            setSearchText,
            resetPage
        );
    }, [props?.layoutType, createMode, navigate, setShouldUpdate, identifierID, currentProduct, searchText]); // Setup headers

    useEffect(() => {
        if (!productName) return;

        setSearchText(productName);
        setCurrentProduct({ productID, productName });
    }, [productName]);
    // #endregion

    // #region METHODS
    const getProductDetails = async () => {
        if (!identifierID) {
            setPageEnabled(false);
            return;
        }

        const formData = {
            companyID: localStorage.getItem("currentCompanyID"),
            retailBrandID: sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand")
        };
        if (isGTIN) formData.gtinID = identifierID;
        else formData.productID = identifierID;

        const response = await Library.makeApiRequest({
            endpoint: "Products/getProductDetails",
            data: formData,
            setLoader: setIsLoadingProduct
        });

        if (response?.data?.status !== 200) return;

        /* Example of data from new endpoint:
        {
            "_id": "69775f1e5fe2c7145dd52730",
            "ourID": "DE5K8NW0U",
            "labelBrandID": "6932e0da268574cdbbf82e0a",
            "labelBrandName": "Hvisk fake - SPY",
            "itemNumber": "2501-105-010090",
            "itemName": "TWIST BRAIDED SOFT STRUCTURE",
            "itemType": "Crossbody",
            "season": "25 VOL. 1",
            "collectionName": null,
            "description": "Handle bag with one compartment, an attachable, adjustable strap and one inner zipper pocket.  Main Fabric: 40% Recycled post-consumer polyester, 18% Recycled pre-consumer polyurethane, 18% Polyurethane, 14% Calcium carbonate, 10% Wood, Lining: 100% Recycled Polyester  H27 cm x W27 cm x D1,5 cm",
            "composition": "Main Fabric: Polyurethane resin 40% Kib 35% Wood fiber powder, 10% Calcium carbonate, light grade 8% Pigment: 6% Other 1% Lining:100% Recycled Polyester",
            "washAndCare": null,
            "gender": "Female",
            "countryOfOrigin": "CN",
            "shortDescription": null,
            "category": null,
            "maintenance": null,
            "productImages": [
                "https://hvisk.spysystem.dk/file/377ac0a6-72c4-49e0-ba93-1dec69ad2133/ace24ab0-347f-4145-b36e-40bfadf4fdc9/IMG_9106-3.png"
            ],
            "totalStock": 0,
            "totalStoresWithStock": 0,
            "itemsSoldPercentage": 100,
            "shelfSpeed": "Fast",
            "colors": [
                {
                    "name": "Black",
                    "hexacode": "000000",
                    "colorCode": "009 Black",
                    "sizes": [
                        {
                            "size": "One size",
                            "barcode": "5714196140994",
                            "weight": "270"
                        }
                    ]
                }
            ]
        } */

        /* Missing fields in the new endpoint response:
        - descriptionTitle
        - customsTariffCode
        - customsTariffText
        - dimensions
        */

        const data = response?.data?.data;
        setProductData(data);
        setPageEnabled(true);

        const productID = data?._id;
        const productName = data?.itemName;
        setCurrentProduct({ productID, productName });
    };

    const resetPage = () => {
        navigate("/products/details");
        setCurrentProduct(null);
        setProductData(null);
        setSearchText("");
        setShouldUpdate(true);
        setPageEnabled(false);
    };
    // #endregion

    // #region RENDER
    return (
        <StyledPage className={`${pageEnabled ? "enabled-form" : "disabled-form"}`}>
            <FullScreenLoader isLoading={isLoadingProduct} />

            <FormContainer
                title={<ProductTitle statusEnum={productData?.statusEnum} />}
                rightComponent={
                    <RightComponent
                        enabled={pageEnabled}
                        dataAsOfDateTime={productData?.dateAsOf}
                        lastUpdatedDateTime={productData?.lastUpdateDate}
                    />
                }>
                <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                    <div className="top-container">
                        <ProductImageCarousel images={productData?.productImages || []} pageEnabled={pageEnabled} />
                        <ShelfSpeedAndStock productData={productData} pageEnabled={pageEnabled} />
                        <QuickInfoFields productData={productData} pageEnabled={pageEnabled} />
                    </div>

                    <ItemVariationsSection productData={productData} pageEnabled={pageEnabled} />
                </div>
            </FormContainer>

            <OtherInfoSection productData={productData} pageEnabled={pageEnabled} />
        </StyledPage>
    );
    // #endregion
}

// #region HEADER SETUP
function setupHeaders(
    props,
    createMode,
    navigate,
    setShouldUpdate,
    productID,
    currentProduct,
    searchText,
    setSearchText,
    resetPage
) {
    const productIDSuffix = currentProduct
        ? `?productID=${currentProduct.productID}&productName=${currentProduct.productName}`
        : "";

    props.setControlBarLeftBtns([
        {
            customSearch: (
                <CustomSearch
                    enabled={true}
                    placeholder="Item name, style nr, ean code"
                    onTextChange={setSearchText}
                    searchValue={searchText}
                    highlighted={searchText.length > 0}
                    autoCompleteAPIData={{
                        useBOCommon: true,
                        BOCommonContext: "Products",
                        apiName: "searchProducts",
                        apiSearchFieldName: "searchText",
                        includeInAPIFormData: { companyID: localStorage.getItem("currentCompanyID") },
                        customMapping: data => {
                            const isProducts = data?.isProducts;
                            const isGTIN = data?.isGTINs;

                            if ((!isProducts && !isGTIN) || !data) return [];

                            const searchResults = isProducts ? data?.products : data?.variants;

                            if (isProducts) {
                                const mappedData = searchResults?.map(item => ({
                                    ...item,
                                    isProduct: true,
                                    value: item._id,
                                    name: item.name
                                }));
                                return mappedData;
                            } else if (isGTIN) {
                                const mappedData = searchResults?.map(item => ({
                                    ...item,
                                    isGTINs: true,
                                    value: item._id,
                                    name: item.name,
                                    barcode: item.barcode
                                }));
                                console.log("Mapped data:", mappedData);
                                return mappedData;
                            }
                        }
                    }}
                    onSelectedItemChange={item => {
                        if (!item) return;

                        if (item?.isProduct)
                            navigate(`/products/details?productID=${item._id}&productName=${item.name}`);
                        else if (item?.isGTINs)
                            navigate(`/products/details?gtinID=${item._id}&productName=${item.name}`);

                        setTimeout(() => {
                            setShouldUpdate(true);
                        }, 100);
                    }}
                    formFieldProps={{
                        requiredType: !productID ? "mandatory" : "simple"
                    }}
                    onClearSearch={() => resetPage()}
                />
            )
        }
    ]);
    props.setControlBarCenterBtns([
        { to: "/products", active: true, disabled: createMode, title: "Live" },
        { to: "/products/my-defaults/seasons", active: false, title: "MY DEFAULTS" }
    ]);
    props.setControlBarRightBtns([
        { to: "/products", active: false, disabled: createMode, title: "View", icon: "eye" },
        {
            to: `/products/stock${productIDSuffix}`,
            active: false,
            disabled: false,
            title: "Sales",
            icon: "bar-chart-05"
        },
        {
            to: `/products/metrics${productIDSuffix}`,
            active: false,
            disabled: false,
            title: "Metrics",
            icon: "bar-line-chart"
        },
        { to: "#", active: true, disabled: false, title: "Details", icon: "microscope" },
        {
            to: `/products/pos-export${productIDSuffix}`,
            active: false,
            disabled: false,
            title: "Export",
            icon: "upload-01"
        }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: <></>
    });
}
// #endregion

// #region SMALL COMPONENTS
const ProductTitle = ({ statusEnum }) => (
    <div style={{ display: "flex", alignItems: "center" }} className="card-title">
        MAIN DATA
        {statusEnum && (
            <div
                className="form-container-tag"
                style={{
                    background: statusEnum === "Active" ? "var(--Buttons-Green-Active)" : "var(--Buttons-Orange-Active)"
                }}>
                {statusEnum}
            </div>
        )}
    </div>
);

const RightComponent = ({ enabled, dataAsOfDateTime, lastUpdatedDateTime }) => {
    const dataAsOfFormatted = formatDefaultDateTime(dataAsOfDateTime, "");
    const lastUpdatedFormatted = formatDefaultDateTime(lastUpdatedDateTime, "--");

    return (
        <div className={`last-update-container ${enabled ? "" : "d-none"}`}>
            <div className="last-update mr-24px d-none">
                Data as of: {dataAsOfFormatted ? `${dataAsOfFormatted}` : "--"}
            </div>
            <div className="last-update">Last update: {lastUpdatedFormatted ?? "--"}</div>
        </div>
    );
};

const QuickInfoFields = ({ productData, pageEnabled }) => (
    <div className="inputs-container">
        <div className="first-portion-input-one invite-input-two">
            <FormField
                type="text"
                title="Brand"
                tooltip="Product brand"
                requiredType={!pageEnabled ? "locked" : "disabledSimple"}
                value={productData?.labelBrandName}
                assistiveTextSpacing={false}
            />
        </div>
        <div className="first-portion-input-one invite-input-two">
            <FormField
                type="text"
                title="Item nr."
                tooltip="Item nr."
                requiredType={!pageEnabled ? "locked" : "disabledSimple"}
                value={productData?.itemNumber}
                assistiveTextSpacing={false}
            />
        </div>
        <div className="first-portion-input-one invite-input-two">
            <FormField
                type="text"
                title="Item name"
                tooltip="Item name"
                requiredType={!pageEnabled ? "locked" : "disabledSimple"}
                value={productData?.itemName}
                assistiveTextSpacing={false}
            />
        </div>
        <div className="first-portion-input-one invite-input-two flex flex-row">
            <FormField
                style={{ width: "50%", marginRight: "5px" }}
                type="text"
                title="Season"
                tooltip="Season"
                requiredType={!pageEnabled ? "locked" : "disabledSimple"}
                value={productData?.season}
                assistiveTextSpacing={false}
            />
            <FormField
                style={{ width: "50%", marginLeft: "5px" }}
                type="date"
                title="Lifecycle ends"
                tooltip="Lifecycle ends"
                requiredType={!pageEnabled ? "locked" : "simple"}
                value={productData?.lifecycleEnds}
                assistiveTextSpacing={false}
                leftContent={<CalendarIcon className="flex" />}
                datePickerPlaceholder=" "
            />
        </div>
    </div>
);
// #endregion
